/**
 * One embedded component instance: the bridge the component sees
 * (`useEmbedContext()` → emit / expose / setState) and the controller the
 * embedding surface holds (call / getState / navigate / remount).
 */

import type { EmbedBridge, EmbedEvent, EmbedInstanceController, EmbedMode } from '@neuro/plugin-sdk/ui';

export const METHOD_WAIT_MS = 5000;

export interface InstanceCallbacks {
  onEvent?: (event: EmbedEvent) => void;
  onStateChange?: (state: Readonly<Record<string, unknown>>) => void;
  navigate?: (path: string) => void;
  remount?: () => void;
}

export class EmbedInstance {
  private methods = new Map<string, (...args: any[]) => unknown>();
  private waiters = new Map<string, Array<() => void>>();
  private state: Record<string, unknown> = {};
  callbacks: InstanceCallbacks = {};

  readonly bridge: EmbedBridge;
  readonly controller: EmbedInstanceController;

  constructor(readonly instanceId: string, readonly componentId: string, private mode: () => EmbedMode) {
    const self = this;
    this.bridge = {
      instanceId,
      componentId,
      get mode() { return self.mode(); },
      emit: (name, payload) => self.emit(name, payload),
      expose: (methods) => self.expose(methods),
      setState: (patch) => self.setState(patch),
    };
    this.controller = {
      instanceId,
      componentId,
      call: (method, ...args) => self.call(method, args),
      hasMethod: (method) => self.methods.has(method),
      getState: () => self.state,
      navigate: (path) => self.callbacks.navigate?.(path),
      remount: () => self.callbacks.remount?.(),
    };
  }

  emit(name: string, payload?: unknown): void {
    this.callbacks.onEvent?.({ name, payload, componentId: this.componentId, instanceId: this.instanceId });
  }

  expose(methods: Record<string, (...args: any[]) => unknown>): () => void {
    for (const [name, fn] of Object.entries(methods)) {
      this.methods.set(name, fn);
      for (const resolve of this.waiters.get(name) ?? []) resolve();
      this.waiters.delete(name);
    }
    return () => {
      for (const [name, fn] of Object.entries(methods)) {
        if (this.methods.get(name) === fn) this.methods.delete(name);
      }
    };
  }

  setState(patch: Record<string, unknown>): void {
    this.state = { ...this.state, ...patch };
    this.callbacks.onStateChange?.(this.state);
  }

  /** Calls an exposed method, waiting briefly for it to be exposed (e.g. while the component loads). */
  async call(method: string, args: unknown[]): Promise<unknown> {
    if (!this.methods.has(method)) {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => {
          const list = this.waiters.get(method)?.filter((w) => w !== done) ?? [];
          if (list.length) this.waiters.set(method, list);
          else this.waiters.delete(method);
          reject(new Error(`"${this.componentId}" does not expose method "${method}"`));
        }, METHOD_WAIT_MS);
        const done = () => { clearTimeout(timer); resolve(); };
        this.waiters.set(method, [...(this.waiters.get(method) ?? []), done]);
      });
    }
    return this.methods.get(method)!(...args);
  }
}
